import React, { useState } from 'react'

import LootHistoryResponseTypes from './LootHistoryResponseTypes'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CircleQuestionMark } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { z } from 'zod'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { Button } from '@/components/ui/button'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useQueryClient } from '@tanstack/react-query'
import { LootHistoryUrlOptions } from 'app/frontend/base/api/team'

const formSchema = z.object({
  loot_history_response_types_attributes: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      excluded: z.boolean(),
      rgba: z.string(),
      synonym_of_id: z.number().nullable(),
      index: z.number(),
    }),
  ),
  loot_history_response_type_order: z.array(z.number()),
})

export type FormValues = z.infer<typeof formSchema>

type Props = {
  responseTypes: wowaudit.LootHistoryResponseType[]
  responseTypeOrder: number[]
  lootHistoryOptions: LootHistoryUrlOptions
}

const LootHistoryResponseTypeModal = ({
  responseTypes,
  responseTypeOrder,
  lootHistoryOptions,
}: Props) => {
  const { team } = useTeam()
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  return (
    <div>
      <Button variant='outline' size='sm' onClick={() => setOpen(true)}>
        Adjust categories
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-w-xl'>
          <DialogHeader>
            <DialogTitle>Response types</DialogTitle>
          </DialogHeader>

          <p>
            Response types in RCLootCouncil can vary wildly, based on the button
            configuration of each individual master looter. You can combine the
            different responses into a configuration that suits you.
          </p>

          <div>
            <div className='flex justify-between items-center font-semibold mb-1'>
              <div>Type</div>
              <div className='flex items-center gap-2'>
                Merged with{' '}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      className='inline-flex items-center justify-center'
                      tabIndex={-1}
                    >
                      <CircleQuestionMark size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side='left'
                    className='font-normal max-w-[20rem]'
                  >
                    Merged response types will not be visible in the statistics
                    table anymore. All awarded loot is treated as the type it is
                    merged with.
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            <FormWrapper<
              Partial<wowaudit.Team> & {
                loot_history_response_types_attributes: wowaudit.LootHistoryResponseType[]
                loot_history_response_type_order: number[]
              }
            >
              key={team?.id}
              formSchema={formSchema}
              extraActions={[
                <Button
                  key='cancel'
                  variant='outline'
                  type='button'
                  size='sm'
                  onClick={() => setOpen(false)}
                >
                  Close
                </Button>,
              ]}
              entityType='team'
              entity={{
                id: team?.id,
                loot_history_response_types_attributes: responseTypes.map(
                  (type, index) => ({
                    ...type,
                    index,
                  }),
                ),
                loot_history_response_type_order: responseTypeOrder,
              }}
              onSave={async (form, data) => {
                const result = (await queryClient.fetchQuery({
                  queryKey: ['teamLootHistory', lootHistoryOptions],
                })) as wowaudit.LootHistory

                form.reset({
                  ...data,
                  loot_history_response_types_attributes:
                    result.loot_history_response_types.map((type, index) => ({
                      ...type,
                      index,
                    })),
                  loot_history_response_type_order:
                    result.loot_history_response_type_order,
                })
              }}
            >
              <LootHistoryResponseTypes />
            </FormWrapper>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default LootHistoryResponseTypeModal
